import { Sparkles, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { AiInsight } from '@/features/ai-insights/types'
import { cn } from '@/utils/cn'
import { GenerateInsightsButton } from './GenerateInsightsButton'
import { InsightCard } from './InsightCard'
import { SpectreThinkingBadge } from './SpectreThinkingBadge'

interface InsightsListProps {
  insights: AiInsight[]
  loading: boolean
  refreshing: boolean
  /** Insights produced during the current session — pinned to top. Caller decides
   * what "current session" means; typically tracked via useRef of probe ids
   * received since mount. */
  sessionProbeIds?: Set<string>
  onAccept: (id: string) => void
  onSkip: (id: string) => void
  onOpenInGraph?: (insight: AiInsight) => void
  onGenerate: () => void
  className?: string
}

const MAX_PINNED = 3

export function InsightsList({
  insights,
  loading,
  refreshing,
  sessionProbeIds,
  onAccept,
  onSkip,
  onOpenInGraph,
  onGenerate,
  className,
}: InsightsListProps) {
  const { t } = useTranslation()
  const pinned = sessionProbeIds
    ? insights.filter(i => sessionProbeIds.has(i.id)).slice(0, MAX_PINNED)
    : []
  const pinnedSet = new Set(pinned.map(i => i.id))
  const main = insights.filter(i => !pinnedSet.has(i.id))

  const isEmpty = !loading && insights.length === 0

  return (
    <div data-slot="ai-insights-list" className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-center justify-between">
        <SpectreThinkingBadge visible={refreshing} />
        {!isEmpty && (
          <GenerateInsightsButton onClick={onGenerate} emphasis="inline" disabled={refreshing}>
            {t('investigations.graph.aiInsights.refresh')}
          </GenerateInsightsButton>
        )}
      </div>

      {pinned.length > 0 && (
        <section className="flex flex-col gap-2">
          <SectionLabel
            icon={<Zap className="size-3" />}
            label={t('investigations.graph.aiInsights.fromLastAction')}
          />
          {pinned.map(i => (
            <InsightCard
              key={i.id}
              insight={i}
              onAccept={onAccept}
              onSkip={onSkip}
              onOpenInGraph={onOpenInGraph}
              pinned
            />
          ))}
        </section>
      )}

      {main.length > 0 && (
        <section className="flex flex-col gap-2">
          {pinned.length > 0 && (
            <SectionLabel label={t('investigations.graph.aiInsights.insights')} />
          )}
          {main.map(i => (
            <InsightCard
              key={i.id}
              insight={i}
              onAccept={onAccept}
              onSkip={onSkip}
              onOpenInGraph={onOpenInGraph}
            />
          ))}
        </section>
      )}

      {isEmpty && (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <div className="flex size-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Sparkles className="size-5" />
          </div>
          <p className="max-w-[16rem] text-xs text-muted-foreground">
            {t('investigations.graph.aiInsights.noInsightsForView')}
          </p>
          <GenerateInsightsButton onClick={onGenerate} emphasis="primary" disabled={refreshing} />
        </div>
      )}
    </div>
  )
}

function SectionLabel({ icon, label }: { icon?: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-3xs font-semibold uppercase tracking-widest text-muted-foreground/70">
        {label}
      </span>
    </div>
  )
}
